<template>
  <div v-if="layout === 'list'">
    <ModernVehicleCardList :vehicle="vehicle" />
  </div>
  <div
    v-else
    class="group relative bg-white dark:bg-black rounded-xl overflow-hidden vehicle-card-animation flex"
    :class="{
      'h-full flex-col': layout === 'grid' || layout === 'compact'
    }"
  >
    <!-- Vehicle Image -->
    <div
      class="relative overflow-hidden"
      :class="{
        'aspect-16/9': layout === 'grid',
        'aspect-square': layout === 'compact'
      }"
    >
      <!-- Background & Conditional Icons Container -->
      <div
        v-if="!imageLoaded || imageError"
        class="absolute inset-0 flex items-center justify-center bg-slate-200 dark:bg-slate-700"
      >
        <!-- Spinner: shows if loading and no error yet -->
        <div v-if="!imageLoaded && !imageError" class="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
        <!-- Error Icon: shows if imageError is true (imageLoaded will also be true) -->
        <svg v-if="imageError" xmlns="http://www.w3.org/2000/svg" class="h-12 w-12 text-slate-400 dark:text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
          <path stroke-linecap="round" stroke-linejoin="round" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      </div>

      <!-- Low quality placeholder (Blurred Image) -->
      <img
        v-if="(vehicle.thumb || vehicle.image) && !imageError"
        :src="vehicle.thumb || vehicle.image"
        :alt="vehicle.title"
        class="absolute inset-0 w-full h-full object-cover transform scale-110 blur-sm opacity-20"
        loading="lazy"
        fetchpriority="low"
      />

      <!-- High quality image -->
      <img
        v-if="vehicle.thumb || vehicle.image"
        class="w-full h-full object-cover hover:scale-105 transition-all duration-500"
        :class="{ 'opacity-0': !imageLoaded || imageError }"
        :src="vehicle.thumb || vehicle.image"
        :alt="vehicle.title"
        loading="lazy"
        decoding="async"
        fetchpriority="high"
        @load="onImageLoad"
        @error="onImageError"
      >

      <!-- Price Badge - Only show in grid/compact mode -->
      <div class="absolute bottom-0 left-0 right-0 bg-linear-to-t from-black/50 to-transparent p-4">
        <div class="flex items-end justify-between">
          <div>
            <div v-if="vehicle.price" class="text-white font-bold text-xl">
              ${{ vehicle.price.toLocaleString() }}
            </div>
            <div v-if="vehicle.perweek" class="text-white/80 text-sm">
              ${{ formatPerWeek(vehicle.perweek) }}/week
            </div>
          </div>
          <FavoriteButton v-if="vehicle.id" :vehicle-id="vehicle.id" class="text-white" />
        </div>
      </div>
    </div>

    <!-- Card Content -->
    <div class="flex-1 flex flex-col p-4">
      <!-- Header with Price for List Mode -->
      <header class="mb-4">
        <div class="flex items-start justify-between gap-4">
          <div>
            <h2
              class="font-medium leading-tight text-slate-900 dark:text-white mb-1 md:mb-2"
              :class="{
                'text-xl': layout === 'grid',
                'text-lg': layout === 'compact'
              }"
            >
              {{ vehicle.title }}
            </h2>
          </div>
        </div>
      </header>

      <!-- Vehicle details -->
      <div class="space-y-4 flex-1">
        <!-- Specs -->
        <div class="grid grid-cols-2 gap-y-3">
          <!-- Body Type -->
          <div class="flex items-center gap-2 md:gap-3 text-muted-foreground">
            <svg class="h-5 w-5 text-primary flex-shrink-0" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M3 9h18v8a2 2 0 01-2 2H5a2 2 0 01-2-2V9zm0 0l2-4h14l2 4" />
            </svg>
            <span class="text-sm">
              {{ vehicle.model?.displayBody?.[0] || vehicle.body?.displayValue?.[0] || 'SUV' }}
            </span>
          </div>

          <!-- Drivetrain -->
          <div class="flex items-center gap-2 md:gap-3 text-muted-foreground">
            <svg class="h-5 w-5 text-primary flex-shrink-0" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M12 3v3m0 3v3m0 3v3m-9-9h3m3 0h3m3 0h3" />
            </svg>
            <span class="text-sm">
              {{ vehicle.drivetrain?.displayValue?.[0] || 'Not specified' }}
            </span>
          </div>

          <!-- Transmission -->
          <div class="flex items-center gap-2 md:gap-3 text-muted-foreground">
            <svg class="h-5 w-5 text-primary flex-shrink-0" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M9 6l6 6-6 6" />
            </svg>
            <span class="text-sm">
              {{ vehicle.transmission?.displayValue?.[0] || 'Not specified' }}
            </span>
          </div>

          <!-- Kilometers -->
          <div class="flex items-center gap-2 md:gap-3 text-muted-foreground">
            <svg class="h-5 w-5 text-primary flex-shrink-0" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="12" cy="12" r="8" />
              <path d="M12 8v4l2 2" />
            </svg>
            <span class="text-sm">
              {{ formatKilometers(vehicle.kms || vehicle.mileage) }}
            </span>
          </div>

          <!-- Fuel Type -->
          <div class="flex items-center gap-2 md:gap-3 text-muted-foreground">
            <svg class="h-5 w-5 text-primary flex-shrink-0" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M19 8C20.6569 8 22 9.34315 22 11V17C22 18.6569 20.6569 20 19 20H16.5C15.9477 20 15.5 19.5523 15.5 19V9C15.5 8.44772 15.9477 8 16.5 8H19Z" />
              <path d="M6 3C6 2.44772 6.44772 2 7 2H12C12.5523 2 13 2.44772 13 3V22H6V3Z" />
              <path d="M6 14H13" />
              <path d="M4 14H5" />
              <path d="M17 14C17 14 19 11.9948 19 11" />
            </svg>
            <span class="text-sm">
              {{ vehicle.fuel?.displayValue?.[0] || 'Not specified' }}
            </span>
          </div>

          <!-- Engine -->
          <div class="flex items-center gap-2 md:gap-3 text-muted-foreground">
            <svg class="h-5 w-5 text-primary flex-shrink-0" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <rect x="2" y="8" width="20" height="8" rx="2" />
              <path d="M6 8V6a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v2" />
              <path d="M6 16v2a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2v-2" />
              <path d="M8 12h8" />
            </svg>
            <span class="text-sm">
              {{ vehicle.engine?.displayValue?.[0] || formatEngine(vehicle) }}
            </span>
          </div>
        </div>
      </div>

      <!-- Actions -->
      <div class="flex items-center gap-2 md:gap-3 mt-auto pt-4 flex-wrap">
        <button
          v-if="showSimilarText"
          @click="searchSimilar"
          class="flex-1 inline-flex justify-center items-center whitespace-nowrap rounded-lg bg-slate-100 px-3 py-2 md:px-4 md:py-2.5 text-xs md:text-sm font-medium text-slate-700 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700 transition-colors"
          title="Search similar vehicles"
        >
          <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 md:h-5 md:w-5" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clip-rule="evenodd" />
          </svg>
          <span class="ml-1.5">Similar</span>
        </button>
        <button
          @click="openQuickView(vehicle.id)"
          class="flex-1 inline-flex justify-center items-center whitespace-nowrap rounded-lg bg-slate-100 px-3 py-2 md:px-4 md:py-2.5 text-xs md:text-sm font-medium text-slate-700 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700 transition-colors"
        >
          Quick View
        </button>
        <NuxtLink
          class="flex-1 inline-flex justify-center items-center whitespace-nowrap rounded-lg bg-primary px-3 py-2 md:px-4 md:py-2.5 text-xs md:text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
          :to="`/vehicle-for-sale/${vehicle.stockid || vehicle.id}/${vehicle.slug}`"
        >
          View Details
        </NuxtLink>
        <button
          @click="addToCart"
          class="flex-1 inline-flex justify-center items-center whitespace-nowrap rounded-lg bg-secondary px-3 py-2 md:px-4 md:py-2.5 text-xs md:text-sm font-medium text-secondary-foreground hover:bg-secondary/90 transition-colors"
          :class="{ 'bg-blue-100 dark:bg-blue-900/30 !text-blue-600 dark:!text-blue-400 hover:!bg-blue-200 dark:hover:!bg-blue-800/50': isClientMounted && cartStore.isInCart(vehicle.id) }"
          :title="isClientMounted && cartStore.isInCart(vehicle.id) ? 'In Cart' : 'Add to cart'"
        >
          <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 md:h-5 md:w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"
            :class="(isClientMounted && cartStore.isInCart(vehicle.id)) ? 'text-blue-600 dark:text-blue-400' : ''" >
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
          <span class="ml-1.5 hidden md:inline">{{ (isClientMounted && cartStore.isInCart(vehicle.id)) ? 'In Cart' : 'Add to Cart' }}</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useVehicleQuickView } from '~/layers/base/composables/useVehicleQuickView';
import { useFavoritesStore } from '~/stores/favorites';
import { useCartStore } from '~/stores/cartStore';
import FavoriteButton from '~/layers/base/components/FavoriteButton.vue';
import { ref, computed, onMounted } from 'vue';
import ModernVehicleCardList from './ModernVehicleCardList.vue';
import { useRouter } from '#imports';
import { useVehiclesStore } from '~/stores/vehicles';
import { storeToRefs } from 'pinia'

const { openQuickView } = useVehicleQuickView();
const favoritesStore = useFavoritesStore();
const cartStore = useCartStore();
const router = useRouter();
const vehiclesStore = useVehiclesStore();
const { vehicles: allVehiclesRef } = storeToRefs(vehiclesStore);

const isClientMounted = ref(false);
const isMounted = ref(false);
const imageLoaded = ref(false);
const imageError = ref(false);

// Handle image load event
const onImageLoad = () => {
  imageLoaded.value = true;
  imageError.value = false;
};

// Handle image error event
const onImageError = () => {
  imageError.value = true;
  imageLoaded.value = true;
};

onMounted(() => {
  isClientMounted.value = true;
  isMounted.value = true;

  // Check if the image might already be in browser cache
  if (props.vehicle.thumb || props.vehicle.image) {
    const img = new Image();
    img.onload = onImageLoad;
    img.onerror = onImageError;
    img.src = props.vehicle.thumb || props.vehicle.image;

    // If the image is already cached, the onload event might not fire
    // Set a timeout to check if the image is complete
    setTimeout(() => {
      if (img.complete && !imageLoaded.value) {
        onImageLoad();
      }
    }, 50);
  } else {
    // If no image src is provided at all, trigger error state for placeholder
    onImageError();
  }
});

// Compute how many similar vehicles exist (based on make/model/badge)
const similarCount = computed(() => {
  const allVehicles = Array.isArray(allVehiclesRef?.value) ? allVehiclesRef.value : [];

  const make = props.vehicle.make?.value?.[0];
  const model = props.vehicle.model?.value?.[0];
  const badge = props.vehicle.badge?.value?.[0];

  if (!make && !model && !badge) return 0;

  let count = 0;
  for (const v of allVehicles) {
    if (!v || v.id === props.vehicle.id) continue;
    const vMake = v?.make?.value?.[0];
    const vModel = v?.model?.value?.[0];
    const vBadge = v?.badge?.value?.[0];

    if (make && vMake !== make) continue;
    if (model && vModel !== model) continue;
    if (badge && vBadge !== badge) continue;

    count++;
    if (count > 2) break;
  }
  return count;
});

const showSimilarText = computed(() => similarCount.value > 2);

const props = defineProps({
  vehicle: {
    type: Object,
    required: true,
    default: () => ({
      id: '',
      slug: '',
      title: '',
      description: '',
      image: '',
      year: null,
      mileage: null,
      price: null,
      perweek: null
    })
  },
  layout: {
    type: String,
    default: 'grid',
    validator: (value: string) => ['grid', 'list', 'compact'].includes(value)
  }
});

// Function to format the perweek value
const formatPerWeek = (perweek: any): string => {
  let value: number;
  if (typeof perweek === 'number') {
    value = perweek;
  } else if (perweek && typeof perweek === 'object' && 'value' in perweek) {
    value = Array.isArray(perweek.value) && perweek.value.length > 0
      ? Number(perweek.value[0])
      : 0;
  } else {
    value = 0;
  }
  return Math.round(value).toString();
};

// Function to search for similar vehicles
const searchSimilar = () => {
  // Build query parameters for similar vehicles
  const query: any = {};
  
  // Add make filter if available
  if (props.vehicle.make?.value?.[0]) {
    query.make = props.vehicle.make.value[0];
  }
  
  // Add model filter if available
  if (props.vehicle.model?.value?.[0]) {
    query.model = props.vehicle.model.value[0];
  }
  
  // Add badge filter if available
  if (props.vehicle.badge?.value?.[0]) {
    query.badge = props.vehicle.badge.value[0];
  }
  
  // If no specific filters are available, fallback to search keywords from title
  if (Object.keys(query).length === 0) {
    const titleParts = props.vehicle.title.split(' ');
    if (titleParts.length >= 2) {
      query.search_keywords = titleParts.slice(0, 3).join(' ');
    }
  }
  
  // Navigate to cars-for-sale page with filters
  router.push({
    path: '/cars-for-sale',
    query
  });
};

function formatKilometers(kms: number | null | undefined): string {
  if (!kms) return 'Not specified';
  return `${kms.toLocaleString()} km`;
}

function formatEngine(vehicle: any): string {
  if (vehicle.engine?.displayValue?.[0]) {
    return vehicle.engine.displayValue[0];
  }

  // Fallback to constructing from fuel type if engine info not available
  if (vehicle.fuel?.displayValue?.[0]) {
    const fuelType = vehicle.fuel.displayValue[0];
    if (fuelType.toLowerCase().includes('diesel')) {
      return 'Diesel';
    }
    if (fuelType.toLowerCase().includes('petrol')) {
      return 'Petrol';
    }
    return fuelType;
  }

  return 'Not specified';
}

// Add to cart action
const addToCart = () => {
  if (props.vehicle.id) {
    cartStore.addToCart(props.vehicle.id);
    // cartStore.openCart(); // Optionally open cart drawer/dialog after adding
  } else {
    console.error("Cannot add to cart: Vehicle ID is missing");
  }
};
</script>

<style scoped>
.vehicle-card-animation {
  opacity: 0;
  transform: translateY(20px);
  animation: fadeInUp 0.5s ease-out forwards;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.vehicle-card-animation {
  box-shadow: 0 10px 15px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -2px rgba(0, 0, 0, 0.04);
}

.vehicle-card-animation:hover {
  transform: translateY(-5px);
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
}

/* Image loading transitions */
img {
  transition: opacity 0.5s ease, transform 0.5s ease;
}

/* Ensure the aspect ratio containers maintain proper dimensions */
.aspect-16\/9 {
  position: relative;
}

.aspect-16\/9::before {
  content: "";
  display: block;
  padding-top: 56.25%; /* 9/16 = 0.5625 = 56.25% */
}

.aspect-square {
  position: relative;
}

.aspect-square::before {
  content: "";
  display: block;
  padding-top: 100%; /* 1/1 = 1 = 100% */
}

.aspect-16\/9 > *, .aspect-square > * {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

/* Loading animation */
@keyframes pulse {
  0%, 100% {
    opacity: 0.6;
  }
  50% {
    opacity: 0.8;
  }
}

.animate-pulse {
  animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Responsive adjustments */
@media (max-width: 640px) {
  .aspect-16\/9::before {
    padding-top: 66.67%; /* 2/3 aspect ratio on mobile for better visibility */
  }
}
</style>